import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';

export type EmailSanitizeOptions = {
  allowDataAttr?: boolean;
  additionalTags?: string[];
  additionalAttributes?: string[];
};

/**
 * Viewport-relative lengths are a trap in an email body. The message renders in
 * a height-auto iframe whose height is DERIVED from its content, so `100vh`
 * resolves against the very number the content is computing — one real
 * newsletter (`min-height: 100vh` on its outer wrapper) grew the frame by
 * ~3,000px on every measurement pass, forever. They are meaningless here
 * regardless: a message is a block in a page, not a viewport. Resolve them
 * against a fixed notional viewport so the value is stable and self-contained.
 */
const NOTIONAL_VIEWPORT_PX = 640;

function neutralizeViewportUnits(css: string): string {
  if (!css || !/\d\s*(svh|lvh|dvh|vmin|vmax|vh)\b/i.test(css)) return css;
  // url(...) is consumed by the first alternative so a path like
  // "/img/12vh.png" is never rewritten.
  return css.replace(
    /url\([^)]*\)|(-?\d*\.?\d+)(svh|lvh|dvh|vmin|vmax|vh)\b/gi,
    (match, value?: string) => {
      if (value === undefined) return match;
      const px = (parseFloat(value) / 100) * NOTIONAL_VIEWPORT_PX;
      return `${Math.round(px * 100) / 100}px`;
    },
  );
}

/**
 * Extract <style> tags and body content from a full HTML document,
 * and process all anchor tags to add target="_blank" and rel="noopener noreferrer".
 * Combined into a single DOMParser pass for performance.
 */
function processAndExtract(html: string): SanitizedEmail {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Process links: add target="_blank" and rel="noopener noreferrer"
  const links = doc.querySelectorAll('a[href]');
  links.forEach((link) => {
    if (!link.hasAttribute('target')) {
      link.setAttribute('target', '_blank');
    }
    const existingRel = link.getAttribute('rel') || '';
    const relValues = existingRel.split(/\s+/).filter(Boolean);
    if (!relValues.includes('noopener')) {
      relValues.push('noopener');
    }
    if (!relValues.includes('noreferrer')) {
      relValues.push('noreferrer');
    }
    link.setAttribute('rel', relValues.join(' '));
  });

  // Viewport units are the only inline-style rewrite. Colours and backgrounds
  // are the sender's design and are rendered as authored — see
  // `supportsDarkMode` for how dark mode is reconciled with them.
  const styledElements = doc.body?.querySelectorAll('[style]') || [];
  const allElements = doc.body?.hasAttribute('style')
    ? [doc.body, ...Array.from(styledElements)]
    : Array.from(styledElements);
  allElements.forEach((el) => {
    const style = el.getAttribute('style');
    if (!style) return;
    const neutralized = neutralizeViewportUnits(style);
    if (neutralized !== style) {
      el.setAttribute('style', neutralized);
    }
  });

  // Collect all style tags from both head and body
  const headStyleTags = Array.from(doc.head?.querySelectorAll('style') || []);
  const bodyStyleTags = Array.from(doc.body?.querySelectorAll('style') || []);
  const allStyleTags = [...headStyleTags, ...bodyStyleTags];

  const styles = allStyleTags
    .map((style) => {
      const css = neutralizeViewportUnits(style.textContent || style.innerHTML || '');
      return css.trim() ? `<style>${css}</style>` : '';
    })
    .filter(Boolean)
    .join('\n');

  // Remove style tags from body to avoid duplicates
  bodyStyleTags.forEach((style) => style.remove());

  const body = doc.body?.innerHTML || html;

  return {
    styles,
    body,
    bodyAttributes: serializeBodyAttributes(doc),
    supportsDarkMode: declaresDarkMode(doc, styles),
  };
}

/**
 * The message's own <body> attributes (already sanitized), serialized for the
 * render frame's <body>. Templates commonly put the page background there
 * (`<body style="background-color:#EBE9EA">` / `bgcolor`), and only the body's
 * CONTENTS travel in `body` — without this the frame shows our canvas instead.
 */
function serializeBodyAttributes(doc: Document): string {
  if (!doc.body || doc.body.attributes.length === 0) return '';
  const shell = (doc.body.cloneNode(false) as HTMLElement).outerHTML;
  return shell.slice('<body'.length, shell.indexOf('>')).trim();
}

/**
 * Whether the message declares that it renders correctly in dark mode — the
 * same opt-in Apple Mail honours: a `color-scheme` / `supported-color-schemes`
 * meta (or CSS property) that lists `dark`, or its own
 * `prefers-color-scheme: dark` rules. A message that declares nothing was
 * designed for a light canvas and must be shown on one; recolouring it means
 * rewriting the sender's colours, which breaks layouts (white content areas
 * on tinted pages vanish).
 */
function declaresDarkMode(doc: Document, styles: string): boolean {
  const metas = doc.querySelectorAll<HTMLMetaElement>(
    'meta[name="color-scheme" i], meta[name="supported-color-schemes" i]',
  );
  for (const meta of Array.from(metas)) {
    if (/\bdark\b/i.test(meta.content || '')) return true;
  }
  return (
    /prefers-color-scheme\s*:\s*dark/i.test(styles) ||
    /(?:^|[\s;{])(?:supported-)?color-scheme\s*:[^;}]*\bdark\b/i.test(styles)
  );
}

/**
 * Extract <style> tags and body content from a full HTML document.
 * Returns styles and body separately for iframe srcdoc rendering.
 */
export function extractStylesAndBody(html: string): { styles: string; body: string } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const headStyleTags = Array.from(doc.head?.querySelectorAll('style') || []);
  const bodyStyleTags = Array.from(doc.body?.querySelectorAll('style') || []);
  const allStyleTags = [...headStyleTags, ...bodyStyleTags];

  const styles = allStyleTags
    .map((style) => {
      const css = style.textContent || style.innerHTML || '';
      return css.trim() ? `<style>${css}</style>` : '';
    })
    .filter(Boolean)
    .join('\n');

  bodyStyleTags.forEach((style) => style.remove());

  const body = doc.body?.innerHTML || html;

  return { styles, body };
}

export function createEmailSanitizeConfig(options: EmailSanitizeOptions = {}): Config {
  const {
    allowDataAttr = false,
    additionalTags = [],
    additionalAttributes = [],
  } = options;

  // Tags to add beyond DOMPurify's default HTML profile
  const extraTags = [
    // Email structure
    'style', 'head', 'meta', 'title', 'noscript',
    // SVG
    'svg', 'path', 'circle', 'rect', 'ellipse', 'line', 'polyline', 'polygon',
    'g', 'defs', 'use', 'symbol', 'linearGradient', 'radialGradient', 'stop',
    'clipPath', 'mask', 'pattern', 'text', 'tspan',
    // Outlook/VML
    'o:p', 'v:shape', 'v:imagedata', 'v:fill', 'v:stroke', 'v:textbox',
    'v:rect', 'v:oval', 'v:roundrect',
    ...additionalTags,
  ];

  // SVG and Outlook attributes to add beyond default HTML
  const extraAttrs = [
    // Head/meta attributes
    'http-equiv', 'content', 'charset', 'property',
    // SVG
    'xmlns', 'xmlns:xlink', 'viewBox', 'preserveAspectRatio', 'd', 'fill',
    'fill-opacity', 'fill-rule', 'stroke', 'stroke-width', 'stroke-opacity',
    'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
    'opacity', 'transform', 'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y', 'x1', 'y1',
    'x2', 'y2', 'points', 'offset', 'stop-color', 'stop-opacity', 'gradientUnits',
    'gradientTransform', 'spreadMethod', 'xlink:href', 'clip-path', 'mask',
    'filter', 'text-anchor', 'font-family', 'font-size', 'font-weight',
    'font-style', 'dx', 'dy',
    // Outlook/VML
    'coordsize', 'coordorigin', 'path', 'fillcolor', 'strokecolor', 'strokeweight',
    'o:allowincell', 'o:button', 'o:hr', 'o:preferrelative', 'o:spt', 'o:title',
    'v:ext', 'v:shapes',
    // Old HTML attributes
    'vlink', 'link',
    ...additionalAttributes,
  ];

  return {
    USE_PROFILES: { html: true },
    ADD_TAGS: extraTags,
    ADD_ATTR: extraAttrs,
    ALLOW_DATA_ATTR: allowDataAttr,
    ALLOW_ARIA_ATTR: true,
    KEEP_CONTENT: true,
    WHOLE_DOCUMENT: true,
    FORCE_BODY: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    SANITIZE_DOM: false,
    FORBID_TAGS: [],
    FORBID_ATTR: [],
  } as Config;
}

export interface SanitizedEmail {
  styles: string;
  body: string;
  /** Serialized attributes of the message's own <body> (style, bgcolor, …). */
  bodyAttributes: string;
  /** The message opts in to dark rendering; otherwise it is shown on a light canvas. */
  supportsDarkMode: boolean;
}

// Register DOMPurify hooks once at module scope instead of per-call
DOMPurify.addHook('uponSanitizeElement', (_node, data) => {
  const forceKeepTags = ['style', 'head', 'meta', 'title'];
  if (forceKeepTags.includes(data.tagName?.toLowerCase() || '')) {
    return;
  }
});

DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  const forceAllowedAttrs = [
    'style', 'class', 'lang', 'href', 'src', 'alt', 'id', 'width', 'height',
    'border', 'cellpadding', 'cellspacing', 'align', 'valign', 'target',
    'rowspan', 'colspan', 'bgcolor', 'color', 'face', 'size', 'clear',
    'vlink', 'link', 'alink', 'text', 'name', 'hspace', 'vspace',
    'role', 'title', 'type', 'http-equiv', 'content', 'charset', 'property',
  ];

  if (forceAllowedAttrs.includes(data.attrName?.toLowerCase())) {
    data.forceKeepAttr = true;
    return;
  }
});

// P8 (PERF_AUDIT): sanitizing is a PURE function of (html, options), yet it ran
// on every mount — re-opening an email or re-rendering a thread paid the full
// DOMPurify + DOMParser cost again each time. An LRU keyed on the input caches
// the result so the second (and later) mount of any message is free. Bounded so
// a long session can't grow it without limit; the delete+set "touch" keeps hot
// messages resident.
const SANITIZE_CACHE_LIMIT = 60;
const sanitizeCache = new Map<string, SanitizedEmail>();

// FNV-1a 32-bit. The cache key must not embed the raw HTML itself — the Map
// would retain a second copy of every cached message's source (tens of MB after
// a session of large newsletters). length + hash stands in for the content; a
// collision additionally requires an identical byte length, so serving a wrong
// entry is vanishingly unlikely across a 60-entry cache.
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function sanitizeCacheKey(html: string, options?: EmailSanitizeOptions): string {
  const o = options || {};
  return [
    o.allowDataAttr ? 1 : 0,
    (o.additionalTags || []).join(','),
    (o.additionalAttributes || []).join(','),
    html.length,
    hashString(html),
  ].join('\u0000');
}

// Styles mirroring the compose editor's quoted-reply presentation
// (QUOTED_REPLY_*_STYLE in tiptapExtensions.ts) so materialized legacy
// content looks like a normal quoted reply.
const MATERIALIZED_HEADER_STYLE = 'margin: 0 0 1em 0;';
const MATERIALIZED_BLOCKQUOTE_STYLE =
  'margin: 0 0 0 1em; padding-left: 1em; border-left: 3px solid #d0d7de; white-space: normal;';

/**
 * The compose editor's serialized (getHTML) format stores quoted history and
 * signatures URL-ENCODED inside data attributes on otherwise-empty divs; the
 * send path expands them before mailing, but draft rows and some historical
 * sent copies were stored in the unexpanded form — their quoted context is
 * invisible when rendered. Decode those blocks into real markup BEFORE
 * DOMPurify runs, so the decoded content is sanitized like everything else.
 */
function materializeEncodedBlocks(html: string): string {
  if (!html.includes('data-quoted-html') && !html.includes('data-signature-html')) {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  // Quoted blocks can nest (a stored reply quoting an earlier stored reply),
  // so loop until none remain; attributes are always removed, so this
  // terminates even when decoding fails.
  for (let pass = 0; pass < 5; pass++) {
    const blocks = template.content.querySelectorAll('div[data-quoted-html]');
    if (blocks.length === 0) break;
    blocks.forEach((el) => {
      const encodedHtml = el.getAttribute('data-quoted-html');
      const encodedHeader = el.getAttribute('data-reply-header');
      el.removeAttribute('data-quoted-html');
      el.removeAttribute('data-reply-header');
      try {
        const header = encodedHeader ? decodeURIComponent(encodedHeader) : '';
        const quoted = encodedHtml ? decodeURIComponent(encodedHtml) : '';
        const headerMarkup = header ? `<p style="${MATERIALIZED_HEADER_STYLE}">${header}</p>` : '';
        const quotedMarkup = quoted
          ? `<blockquote style="${MATERIALIZED_BLOCKQUOTE_STYLE}">${quoted}</blockquote>`
          : '';
        if (headerMarkup || quotedMarkup) {
          el.innerHTML = `${headerMarkup}${quotedMarkup}`;
        }
      } catch (error) {
        console.warn('[emailHtmlSanitizer] Failed to decode quoted content block', error);
      }
    });
  }

  template.content.querySelectorAll('div[data-signature-html]').forEach((el) => {
    const encoded = el.getAttribute('data-signature-html');
    el.removeAttribute('data-signature-html');
    if (!encoded || el.innerHTML.trim()) return;
    try {
      el.innerHTML = decodeURIComponent(encoded);
    } catch (error) {
      console.warn('[emailHtmlSanitizer] Failed to decode signature block', error);
    }
  });

  return template.innerHTML;
}

export function sanitizeEmailHtml(html: string, options?: EmailSanitizeOptions): SanitizedEmail {
  if (!html) {
    return { styles: '', body: '', bodyAttributes: '', supportsDarkMode: false };
  }

  const key = sanitizeCacheKey(html, options);
  const cached = sanitizeCache.get(key);
  if (cached) {
    // LRU touch: re-insert so this stays at the "most recently used" end.
    sanitizeCache.delete(key);
    sanitizeCache.set(key, cached);
    return cached;
  }

  const config = createEmailSanitizeConfig(options);
  const sanitized = DOMPurify.sanitize(materializeEncodedBlocks(html), config);

  // Single DOMParser pass for link processing + style/body extraction
  const result = processAndExtract(sanitized);

  sanitizeCache.set(key, result);
  if (sanitizeCache.size > SANITIZE_CACHE_LIMIT) {
    const oldest = sanitizeCache.keys().next().value;
    if (oldest !== undefined) {
      sanitizeCache.delete(oldest);
    }
  }
  return result;
}
